<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useSmarticoMissions } from '@/stores/smartico/missions';
import { useAuthStore } from '@/stores/auth';
import type {
  AffiliateConfig,
  BannerItem,
  AffiliateDetailsConfig,
} from '~/utils/perfilOficialConfigs';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';

const { getResponsiveBannerUrl } = useResponsiveBannerUrl();

const props = defineProps<{
  banner: BannerItem[];
  affiliateDetails?: AffiliateDetailsConfig;
  config: AffiliateConfig;
  isRoletasPage: boolean;
  promocode?: string;
}>();

const avatarBorderColor = computed(() =>
  props.isRoletasPage ? '#FFD700' : '#00C9FF',
);

const avatarGradient = computed(() => {
  return props.isRoletasPage
    ? 'background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, #FFD700 45.19%, #A0522D 100%)'
    : 'background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, #00C9FF 45.19%, #0003BE 100%)';
});

const windowWidth = ref(0);
const isLoading = ref(true);
const imagesLoaded = ref(0);
const totalImages = computed(() => props.banner.length);

const promocodeStore = usePromocodeStore();

const isMobile = computed(() => {
  const mobile = windowWidth.value < 600;

  return mobile;
});

const aspectRatioPadding = computed(() => {
  return isMobile.value ? '42.23%' : '33.07%';
});

const { loadMissions } = useSmarticoMissions();

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);

// Update windowWidth on resize
function updateWindowWidth() {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  loadMissions();
  updateWindowWidth();
  window.addEventListener('resize', updateWindowWidth);
});

function handleImageLoad() {
  imagesLoaded.value++;
  if (imagesLoaded.value === totalImages.value) {
    nextTick(() => {
      isLoading.value = false;
    });
  }
}

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth);
});

// O resgate saiu daqui e virou o modal global do promocode
// (`components/promocode/Modal.vue`): o formulário inline não passava pelo gate
// de elegibilidade (`is_eligible`) nem oferecia os Termos e Condições, então
// jogador inelegível só descobria no erro do core. Uma implementação só, e o
// banner mantém o atalho.
//
// `props.promocode` (código pronto que a página repassa) viaja junto e chega ao
// input do modal já preenchido.
function openPromocode() {
  promocodeStore.open({ code: props.promocode });
}
</script>

<template>
  <div
    class="relative w-full md:rounded-lg -mt-3 mb-3.5 select-none md:border md:border-[#1D2329] md:border-solid overflow-hidden"
    :class="{ 'bg-black': props.isRoletasPage }"
  >
    <div
      class="relative"
      :style="{ paddingTop: aspectRatioPadding }"
    >
      <!-- Skeleton loader / Placeholder while loading -->
      <div
        v-if="isLoading || props.banner.length === 0"
        class="absolute inset-0 z-0 h-auto w-full rounded-t-lg bg-[#00000040] animate-pulse"
      ></div>

      <Swiper
        v-if="props.banner?.length > 0"
        :autoplay="{
          delay: 8000,
          disableOnInteraction: false,
        }"
        class="absolute inset-0 z-0 h-auto w-full rounded-t-lg"
        :modules="[Autoplay]"
        :slides-per-view="1"
        :space-between="0"
      >
        <SwiperSlide
          v-for="(bannerItem, index) in props.banner"
          :key="index"
        >
          <NuxtImg
            :alt="bannerItem.alt"
            class="h-auto w-full rounded-t-lg select-none object-fill -mt-1 md:-mt-2"
            loading="eager"
            :src="getResponsiveBannerUrl(bannerItem.image)"
            @dragstart.prevent
            @error="handleImageLoad"
            @load="handleImageLoad"
          />
        </SwiperSlide>
      </Swiper>
    </div>

    <!-- Affiliate Section - OVERLAY on banner -->
    <div
      v-if="props.affiliateDetails"
      class="absolute bottom-0 left-0 right-0 z-10 p-4 md:pt-7"
      :style="
        props.isRoletasPage
          ? 'background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 26.63%);'
          : 'background: linear-gradient(180deg, rgba(1, 6, 18, 0) 0%, #020716 26.63%);'
      "
    >
      <div
        class="w-full flex flex-col md:flex-row flex-wrap justify-between items-start md:items-center md:pt-3"
      >
        <!-- Left - Affiliate Profile -->
        <div class="flex items-center relative">
          <div
            class="w-[70px] h-[70px] sm:w-24 sm:h-24 rounded-full border-2 border-solid overflow-hidden absolute -top-5 sm:-top-10 left-0"
            :style="[{ borderColor: avatarBorderColor }, avatarGradient]"
          >
            <!-- Simulating a profile picture -->
            <NuxtImg
              v-if="props.affiliateDetails.avatarUrl"
              alt="Avatar Affiliate"
              class="object-cover"
              draggable="false"
              :src="props.affiliateDetails.avatarUrl"
            />
          </div>

          <div class="ml-[82px] sm:ml-28">
            <div class="flex items-center gap-1.5 space-x-1 mb-0.5">
              <h1
                class="text-white text-center text-sm md:text-lg font-bold leading-7"
              >
                {{ props.affiliateDetails.name }}
              </h1>
              <NuxtIcon
                v-if="props.affiliateDetails.iconName"
                class="text-white text-[20px]"
                :filled="true"
                :name="props.affiliateDetails.iconName"
              />
            </div>

            <p
              class="self-stretch text-[rgba(255,255,255,0.50)] text-xs md:text-sm font-medium leading-[18px]"
            >
              {{ props.affiliateDetails.title }}
            </p>
          </div>
        </div>

        <!-- Right - Promotional Code (Desktop): atalho pro modal do promocode.
             A caixa de 64px do banner fica igual; o que era input + botão virou
             um botão só, porque quem valida e resgata agora é o modal. -->
        <div
          v-if="isAuthenticated && props.config.showPromocodeInput"
          class="h-[64px] shrink-0 rounded-[10px] p-[10px] w-full md:w-auto hidden lg:block border backdrop-blur-[10px] border-solid border-[#1D2329]"
        >
          <button
            class="flex h-full w-full min-w-[260px] items-center justify-center gap-[7px] rounded-md px-6 text-center text-sm font-extrabold leading-3 text-white [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984]"
            type="button"
            @click="openPromocode"
          >
            {{ props.affiliateDetails?.redeemButtonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- Bottom - Promotional Code mobile: mesmo atalho, na faixa de 32px que a
       dupla input + botão ocupava. -->
  <div
    v-if="isAuthenticated && props.config.showPromocodeInput"
    class="h-[32px] shrink-0 -mt-[12px] mb-4 w-full block lg:hidden"
  >
    <button
      class="flex h-full w-full items-center justify-center gap-[7px] rounded-md text-center text-[10px] font-extrabold leading-3 !text-white sm:text-sm [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984]"
      type="button"
      @click="openPromocode"
    >
      {{ props.affiliateDetails?.redeemButtonText }}
    </button>
  </div>
</template>
